import { Wallet } from 'lucide-react';
import { SectionCardHeader } from '../../app/section-card-header.js';
import { formatCurrency } from '@hue/design-system';
import { useQuery } from '@tanstack/react-query';
import { salesApi } from '../../lib/api.js';

export interface ClientCreditSummaryProps {
  clientId: string;
  businessUnitId?: string | undefined;
  unitName?: string | undefined;
  compact?: boolean | undefined;
  prepaid?: boolean | undefined;
}

export function ClientCreditSummary({
  clientId,
  businessUnitId,
  unitName,
  compact,
  prepaid,
}: ClientCreditSummaryProps) {
  const credit = useQuery({
    queryKey: ['company', 'sales', 'client-credit', clientId, businessUnitId ?? null],
    queryFn: () => salesApi.clientCredit(clientId, businessUnitId),
    retry: false,
  });
  return (
    <section
      aria-label="Crédito do cliente"
      className={
        compact
          ? 'mt-2 text-xs font-normal text-content-secondary'
          : 'rounded-2xl border border-surface-border bg-surface-card shadow-soft'
      }
    >
      {compact ? (
        <p className="font-semibold">Crédito do cliente</p>
      ) : (
        <SectionCardHeader title="Crédito do cliente" icon={Wallet} />
      )}
      <div className={compact ? undefined : 'p-4'}>
        {credit.isLoading ? (
          <p role="status">Consultando saldo…</p>
        ) : credit.error ? (
          <p role="alert">Não foi possível consultar o crédito. O saldo não está confirmado.</p>
        ) : credit.data ? (
          credit.data.balances.length === 0 ? (
            <p>Sem crédito disponível.</p>
          ) : (
            <div className="grid gap-1">
              {credit.data.balances.map((balance) => (
                <div key={balance.currency}>
                  <p className="font-semibold tabular-nums">
                    {formatCurrency(
                      businessUnitId ? balance.availableCents : balance.totalCents,
                      balance.currency,
                    )}{' '}
                    {businessUnitId
                      ? `disponíveis em ${unitName ?? 'esta unidade'}`
                      : 'nas unidades do seu acesso'}
                  </p>
                  {businessUnitId && balance.totalCents > balance.availableCents ? (
                    <p>
                      {formatCurrency(
                        balance.totalCents - balance.availableCents,
                        balance.currency,
                      )}{' '}
                      restritos a outras unidades.
                    </p>
                  ) : null}
                </div>
              ))}
              {!businessUnitId ? (
                <p>Escolha uma unidade para consultar quanto pode usar nela.</p>
              ) : null}
            </div>
          )
        ) : null}
        {!compact ? (
          <p className="mt-2 text-xs text-content-muted">
            {prepaid
              ? 'Esta sessão usa o pacote contratado. Não exige sinal nem aplica crédito monetário.'
              : 'O crédito só é usado quando a recepção escolhe o valor em Pagamentos.'}
          </p>
        ) : null}
      </div>
    </section>
  );
}
